import { type ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useFetcher, useParams } from '@remix-run/react';

import Button from '~/components/Button';
import StatusInputs from '~/components/Forms/StatusInputs';
import useToast, { type ToastData } from '~/hooks/useToast';

type Props = {
  showSubmitButton?: boolean;
  onSuccess?: () => void;
  children?: ({ isLoading, submitForm }: { isLoading: boolean; submitForm: () => void }) => ReactNode;
};

const ResubscribeAction = ({ children, onSuccess, showSubmitButton = true }: Props) => {
  const fetcher = useFetcher<{
    toast?: ToastData;
  }>();
  const params = useParams();
  const { subscriberId } = params;
  const fetcherData = fetcher.data;
  const status = fetcherData?.toast?.status;
  const isSubmitting = fetcher.state === 'submitting';
  const formRef = useRef<HTMLFormElement>(null);
  const { t } = useTranslation();

  useToast(fetcherData?.toast);

  useEffect(() => {
    if (status === 'success') {
      onSuccess?.();
    }
  }, [status]);

  const submitForm = () => {
    if (formRef.current) {
      fetcher.submit(formRef.current);
    }
  };

  return (
    <fetcher.Form ref={formRef} method="post" action="/resubscribe">
      <input hidden name="subscriber_id" defaultValue={subscriberId} />
      <StatusInputs
        name="resubscribe"
        errorMessage={t('account_actions.downgrade.toast.error')}
        successMessage={t('account_actions.downgrade.toast.resubscribed')}
      />
      {showSubmitButton && (
        <Button type="submit" variant="primary" isLoading={isSubmitting}>
          {isSubmitting ? `${t('shared.actions.resubscribing')}…` : t('shared.cta.resubscribe')}
        </Button>
      )}
      {children?.({ isLoading: isSubmitting, submitForm })}
    </fetcher.Form>
  );
};

export default ResubscribeAction;
